import { Fragment, memo, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { SubNavItem as SubNavItemInterface } from './types';
import { useNavigation } from './NavigationContext';
import { isTabShown } from './navResolution';
import { useCompany } from 'context/company';
import { useUser } from 'context/user';
import useTeamScopedInvoiceCounts from 'hooks/useTeamScopedInvoiceCounts';
import { useRouteAuthorization } from 'hooks/useRouteAuthorization';
import { ContractorRoutes } from 'routes';

interface SubNavItemProps {
  item: SubNavItemInterface;
  index: number;
  isExpanded?: boolean;
  setSubNavExpanded?: any;
}

const ContractorsCountBadge = ({ isShown, total }) => {
  if (!isShown) return null;
  if (!total) return null;
  return (
    <div className="absolute -top-1.5 left-3 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[7px] text-white">
      {total}
    </div>
  );
};

const MemoizedContractorsCountBadge = memo(ContractorsCountBadge);

const CategorySubItemTooltip = ({ name, children }: { name: string; children: React.ReactNode }) => {
  const [hovered, setHovered] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, []);

  const onEnter = useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
    setHovered(true);
    if (ref.current) setRect(ref.current.getBoundingClientRect());
  }, []);

  const onLeave = useCallback((e: React.MouseEvent) => {
    const related = e.relatedTarget;
    if (related instanceof Node && (ref.current?.contains(related) || tipRef.current?.contains(related))) return;
    timeout.current = setTimeout(() => setHovered(false), 100);
  }, []);

  return (
    <div ref={ref} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={tipRef}
            className={`fixed z-[9999] whitespace-nowrap rounded-md border border-gray-100 bg-white px-3 py-2 text-sm font-medium shadow-lg ring-1 ring-black ring-opacity-5 transition-[opacity,transform] duration-200 before:absolute before:-left-4 before:top-0 before:h-full before:w-4 dark:ring-white/10 ${
              hovered ? 'pointer-events-auto translate-x-0 opacity-100' : 'pointer-events-none -translate-x-2 opacity-0'
            }`}
            style={{
              top: rect ? rect.top : 0,
              left: rect ? rect.right + 8 : 80,
            }}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
          >
            {name}
          </div>,
          document.body,
        )}
    </div>
  );
};

export const SubNavItem: React.FC<SubNavItemProps> = ({ item, index, isExpanded, setSubNavExpanded }) => {
  const router = useRouter();
  const { currentCompany, roleType } = useUser();
  const { company } = useCompany();
  const isAdmin = roleType === 'manager';
  const { onboardingFlowComplete } = company?.w2Payroll || {};
  const { activeSubNav, activeSubSideNav, isMobileMenuOpen, toggleMobileMenu, isSidebarCollapsed } = useNavigation();
  const { isAllowed } = useRouteAuthorization();
  const { openInvoicesCount, approvedInvoicesCount } = useTeamScopedInvoiceCounts({ isAdminPoV: isAdmin });

  // The section header is active when this section is current but no specific tab is (activeSubSideNav
  // === -1). When a tab is active the tab carries the highlight, not the header.
  const isSubNavActive = activeSubSideNav !== -1 ? false : index === activeSubNav;

  const total = useMemo(() => openInvoicesCount + approvedInvoicesCount, [approvedInvoicesCount, openInvoicesCount]);

  const [isHovered, setIsHovered] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const listItemRef = useRef<HTMLLIElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const itemHref =
    ContractorRoutes.EmployerProfile === item.href
      ? ContractorRoutes.EmployerProfile.replace('[companyID]', currentCompany?.w2Payroll?.checkId)
      : item.href;
  const hasADropdown = item.subNavs && !item.isCustomElement && !item.subNavs.every((i) => i.isShownInBar === false);
  const shownSubNavs = item.subNavs?.filter(isTabShown);

  const toggleSubMenu = async (e: MouseEvent, item: SubNavItemInterface) => {
    e.preventDefault();
    if (!hasADropdown) {
      // if this is a link, and not an expandable menu
      const target = itemHref;
      setSubNavExpanded(null);
      if (!target) {
        // Defensive: a nav item without an href (e.g. a section header) shouldn't
        // crash router.push(undefined) — just expand the group if there is one.
        if (!isCollapsed && shownSubNavs.length > 0) setSubNavExpanded(item.name);
        return;
      }
      if (target === router.pathname) {
        // If the link is the current page, don't do anything
        return;
      } else {
        // If the link is not the current page, navigate to it
        await router.push(target);
      }
    } else {
      // find the subNav in the item which is the Overview page and navigate to it. it is usually the first subnav
      const target = shownSubNavs.find((subNav) => subNav.name === 'Overview')?.href;
      if (target && target !== router.pathname) {
        await router.push(target);
      }
      // The sidebar's active-route check already keeps the current section open. Toggling it here
      // collapses the tabs for a frame before that check re-expands them, which looks like a flicker.
      if (!isCollapsed && index !== activeSubNav) {
        setSubNavExpanded(item.name);
      }
    }
    toggleMobileMenu();
  };

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovered(true);
    if (listItemRef.current) {
      setRect(listItemRef.current.getBoundingClientRect());
    }
  }, []);

  const handleMouseLeave = useCallback((e: React.MouseEvent) => {
    const relatedTarget = e.relatedTarget;
    // If mouse moved to the tooltip or the li (the other paired element), don't hide
    if (
      relatedTarget instanceof Node &&
      (listItemRef.current?.contains(relatedTarget) || tooltipRef.current?.contains(relatedTarget))
    ) {
      return;
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 100);
  }, []);

  // On mobile (isMobileMenuOpen), always show expanded regardless of collapse state
  const isCollapsed = isSidebarCollapsed && !isMobileMenuOpen;

  const linkClass = `relative flex items-center ${
    isCollapsed ? 'justify-center' : 'justify-between'
  } rounded-md font-medium text-neutral-800 hover:bg-neutral-100`;
  // When collapsed the tabs are hidden, so the section icon itself carries the active state for any
  // page within the section (there is no visible tab to highlight in its place).
  const isNavActive = isSubNavActive || (isCollapsed && index === activeSubNav);

  // Do not show dropdown icon (make it an exception) for US Payroll when being setup
  // Hide the "US Payroll" item if onboardingFlowComplete is false
  if (item.name === 'US Payroll' && !onboardingFlowComplete) {
    return null;
  }

  return (
    <li
      key={item.name}
      ref={listItemRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={isMobileMenuOpen ? 'w-full' : 'w-auto lg:w-full'}
    >
      {isCollapsed &&
        !item.category &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={tooltipRef}
            className={`fixed z-[9999] rounded-md border border-gray-100 bg-white py-1 text-left shadow-lg ring-1 ring-black ring-opacity-5 transition-[opacity,transform] duration-200 before:absolute before:-left-4 before:top-0 before:h-full before:w-4 dark:ring-white/10 ${
              hasADropdown ? 'w-48' : 'whitespace-nowrap px-3 py-2 text-sm font-medium'
            } ${
              isHovered
                ? 'pointer-events-auto translate-x-0 opacity-100'
                : 'pointer-events-none -translate-x-2 opacity-0'
            }`}
            style={{
              top: rect ? rect.top : 0,
              left: rect ? rect.right + 8 : 80,
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {hasADropdown ? (
              <>
                <div className="mb-1 border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-900">
                  {item.name}
                </div>
                {shownSubNavs?.map((subNavItem, subNavIndex) => {
                  if (!isAllowed(subNavItem?.onlyShownTo)) return null;
                  const isActive = activeSubSideNav === subNavIndex && index === activeSubNav;
                  return (
                    <Link
                      key={subNavItem.href}
                      href={subNavItem.href}
                      className={`block px-4 py-2 text-sm hover:bg-neutral-100 hover:text-therablue ${
                        isActive ? 'bg-neutral-50 font-medium text-therablue' : 'text-neutral-700'
                      }`}
                      onClick={() => {
                        toggleMobileMenu();
                        setIsHovered(false);
                      }}
                    >
                      {subNavItem.name}
                    </Link>
                  );
                })}
              </>
            ) : (
              item.name
            )}
          </div>,
          document.body,
        )}
      {!isAllowed(item?.onlyShownTo) ? null : item.category ? (
        !isCollapsed && <p className="mb-1.5 pl-3.5 text-sm font-medium text-gray-400">{item.category}</p>
      ) : !item?.href ? (
        <div
          role="button"
          onClick={(e) => toggleSubMenu(e, item)}
          className={`relative z-20 rounded-md ${isNavActive ? `${!isCollapsed ? 'navigation_active_2' : ''} bg-neutral-100` : 'bg-transparent'}`}
        >
          <span className={`${linkClass} w-full`}>
            <span
              className={`navigation_active_2_span flex items-center py-1.5 ${isCollapsed ? 'justify-center' : 'px-2.5'}`}
            >
              <div className="relative flex h-6 w-6 items-center justify-center">
                <span
                  className={`nav_icon flex h-full w-full items-center justify-center ${isNavActive ? 'navActive_icon' : ''}`}
                >
                  {item.icon}
                </span>
                {!isCollapsed && (
                  <MemoizedContractorsCountBadge isShown={item.name.toUpperCase() === 'CONTRACTORS'} total={total} />
                )}
              </div>
              {!isCollapsed && (
                <span className={`ml-3 text-sm ${isNavActive ? 'text-therablue dark:text-white' : 'text-neutral-600'}`}>
                  {item.name}
                </span>
              )}
            </span>
          </span>
        </div>
      ) : (
        <Link href={itemHref} onClick={(e) => toggleSubMenu(e, item)}>
          <span
            className={`${linkClass} ${isNavActive ? `${!isCollapsed ? 'navigation_active_2' : ''} bg-neutral-100` : ''}`}
          >
            <span
              className={`navigation_active_2_span flex items-center text-nowrap py-1.5 ${isCollapsed ? 'w-full justify-center' : 'px-2.5'}`}
            >
              <div className="relative flex h-6 w-6 items-center justify-center">
                <span
                  className={`nav_icon flex h-full w-full items-center justify-center ${isNavActive ? 'navActive_icon' : ''}`}
                >
                  {item.icon}
                </span>
              </div>
              {!isCollapsed && (
                <>
                  <span
                    className={`ml-3 text-sm ${isNavActive ? 'text-therablue dark:text-white' : 'text-neutral-600'}`}
                  >
                    {item.name}
                  </span>
                  {item.isNew && (
                    <span className="ml-2 rounded-full bg-therablue px-2 py-[3px] text-[10px] font-medium text-white">
                      NEW
                    </span>
                  )}
                  {item.isBeta && (
                    <span className="ml-2 rounded-full bg-therablue px-2 py-[3px] text-[10px] font-medium text-white">
                      BETA
                    </span>
                  )}
                </>
              )}
            </span>
          </span>
        </Link>
      )}

      <div
        className={`sub-item relative z-10 transition-all duration-200 ease-in-out ${
          isExpanded && (!isCollapsed || item.category)
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none absolute -translate-y-8 opacity-0'
        }`}
      >
        {isExpanded &&
          shownSubNavs?.map((subNavItem, subNavIndex) => {
            if (!isAllowed(subNavItem?.onlyShownTo)) {
              return null;
            }
            const isActive = activeSubSideNav === subNavIndex && index === activeSubNav;
            const linkContent = (
              <Link
                href={subNavItem.href}
                key={subNavItem.href}
                className={`rounded-md hover:bg-neutral-100`}
                onClick={toggleMobileMenu}
              >
                <div
                  className={`relative top-0.5 rounded-md hover:bg-neutral-100 ${item.category ? 'my-1 px-2' : 'ml-10'} my-0.5 ${
                    isActive
                      ? `${!isCollapsed ? 'navigation_active before:!top-[50%] before:!-translate-y-1/2 [&_.with-category::before]:!top-[50%] [&_.with-category::before]:!-translate-y-1/2' : ''} rounded-md bg-neutral-100`
                      : ''
                  }`}
                >
                  {item.category ? (
                    <div
                      className={`with-category flex items-center py-1.5 ${
                        isCollapsed ? 'w-full justify-center' : 'gap-3 px-1.5'
                      }`}
                    >
                      <span className={`nav_icon flex w-6 justify-center ${isActive ? 'navActive_icon' : ''}`}>
                        {subNavItem.icon}
                      </span>
                      {!isCollapsed && (
                        <span
                          className={`text-sm font-medium ${isActive ? 'text-therablue dark:text-white' : 'text-neutral-600'}`}
                        >
                          {subNavItem.name}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span
                      className={`no-category relative flex h-9 items-center text-nowrap rounded-md pl-1.5 text-sm font-medium hover:bg-neutral-100 ${isActive ? 'text-therablue dark:text-white' : 'text-neutral-600'}`}
                    >
                      {subNavItem.name}
                    </span>
                  )}
                </div>
              </Link>
            );
            return isCollapsed && item.category ? (
              <CategorySubItemTooltip key={subNavItem.href} name={subNavItem.name}>
                {linkContent}
              </CategorySubItemTooltip>
            ) : (
              <Fragment key={subNavItem.href}>{linkContent}</Fragment>
            );
          })}
      </div>
    </li>
  );
};
